import React, { useEffect, useState } from 'react';
import Button from '../../../../../../components/ui/buttons/Button';
import Pagination from '../../../../../../components/ui/pagination/Pagination';
import { camelizeKeys } from '../../../../../../utils/camelizeKeys';
import { Boxes, Trash2 } from 'lucide-react';
import { formatCurrency } from '../../../../../../utils/formatters';
import CustomRoundedButton from '../../../../../../components/ui/buttons/CustomRoundedButton';
import { useToast } from '../../../../../../components/ui/toast/ToastContext';
import { useConfirm } from '../../../../../../components/ui/confirm/ConfirmContext';
import { fetchWithCsrf } from '../../../../../../utils/fetchWithCsrf';
import OtherModal from '../../../../../../components/modals/properties/OtherModal';

const initialPagination = {
  currentPage: 1,
  totalPages: 1,
  totalCount: 0,
};

const OtherTab = ({ clientId, scope, canWrite }) => {
  const [total, setTotal] = useState(0);
  const [others, setOthers] = useState([]);
  const [pagination, setPagination] = useState(initialPagination);
  const [showCreateOtherModal, setShowCreateOtherModal] = useState(false);
  const [selectedOtherId, setSelectedOtherId] = useState(null);
  const { addToast } = useToast();
  const confirm = useConfirm();

  useEffect(() => {
    fetchData(1);
  }, [clientId, scope]);

  const buildQuery = (params) =>
    Object.entries(params)
      .filter(([, v]) => v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&');

  const fetchData = (page = 1) => {
    const query = buildQuery({ page, scope });

    fetch(`/app/planner/client/${clientId}/dashboard/properties/others?${query}`)
      .then((res) => res.json())
      .then((data) => {
        const camelData = camelizeKeys(data);
        setOthers(camelData.others);
        setPagination(camelData.pagination);
        setTotal(camelData.total);
      });
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: 'EXCLUIR ATIVO?',
      description: `Tem certeza que deseja excluir o ativo?`,
      confirmText: 'EXCLUIR',
    });

    if (!ok) return;

    try {
      const res = await fetchWithCsrf(`/app/planner/client/${clientId}/dashboard/properties/others/${id}`, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.message || 'Erro ao excluir o ativo');
      }

      addToast({ type: 'success', message: 'Ativo excluído com sucesso!' });
      fetchData(pagination.currentPage);
    } catch (err) {
      addToast({ type: 'error', message: err.message });
    }
  };

  const handlePageChange = (newPage) => {
    fetchData(newPage);
  };

  const handleModalSuccess = () => {
    setShowCreateOtherModal(false);
    setSelectedOtherId(null);
    fetchData(pagination.currentPage);
  };

  return (
    <div className="h-full flex flex-col gap-4">
      {/* Título */}
      <h2 className="text-xl text-blueDarker font-semibold">Lista de outros bens</h2>

      {others.length > 0 ? (
        <div className="overflow-x-auto">
          {/* HEADER */}
          <div className="px-2">
            <div className="flex items-center gap-2 text-xs text-tealBase font-medium">
              <div className="w-12 shrink-0" />
              <div className="w-96 shrink-0">Identificação</div>
              <div className="w-28 shrink-0">Tipo</div>
              <div className="w-24 shrink-0">Natureza</div>
              <div className="flex-1">Valor</div>
              <div className="w-24 shrink-0 text-right" />
            </div>
          </div>

          {/* LISTA */}
          <div className="mt-1 flex flex-col gap-1">
            {others.map((item, index) => (
              <React.Fragment key={index}>
                <div className="bg-blueLight rounded-xl px-3 py-3">
                  <div className="flex items-center gap-2 text-sm text-blueSteel">
                    {/* ÍCONE */}
                    <div className="w-12 shrink-0 flex justify-center">
                      <span className="w-9 h-9 flex items-center justify-center rounded-full bg-white">
                        <Boxes className="text-tealMedium" size={18} strokeWidth={2} />
                      </span>
                    </div>

                    {/* Identificação */}
                    <div
                      className="w-96 shrink-0 text-blueDarker font-semibold truncate text-base"
                      title={item.name || '—'}
                    >
                      {item.name || '—'}
                    </div>

                    {/* Tipo */}
                    <div className="w-28 shrink-0 truncate">{item.assetType || '—'}</div>

                    {/* Natureza */}
                    <div className="w-24 shrink-0 truncate">{item.isPersonal ? 'Particular' : 'Comum'}</div>

                    {/* Valor */}
                    <div className="flex-1 min-w-0 truncate">{formatCurrency(item.value)}</div>

                    {/* Ações */}
                    <div className="w-24 shrink-0 flex items-center justify-end gap-4 pr-1">
                      {canWrite && (
                        <>
                          <button onClick={() => handleDelete(item.id)} title="Excluir" disabled={scope === 'couple'}>
                            <Trash2
                              className={scope === 'couple' ? 'cursor-not-allowed text-gray-400' : 'text-redBase'}
                              size={18}
                            />
                          </button>
                          <CustomRoundedButton
                            disabled={scope === 'couple'}
                            size="sm"
                            onClick={() => setSelectedOtherId(item.id)}
                          >
                            EDITAR
                          </CustomRoundedButton>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* espaçador entre linhas */}
                <div className="h-2" />
              </React.Fragment>
            ))}
          </div>
          {pagination.totalPages > 1 && (
            <div className="mt-4">
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      ) : (
        <p>Ainda sem registros.</p>
      )}

      {/* Footer */}
      <div className="mt-auto flex justify-between items-center">
        <div className="flex font-medium items-center gap-2">
          <span className="text-sm text-tealBase">TOTAL: </span>
          <span className="text-xl blueDark">{formatCurrency(total)}</span>
        </div>

        <div className="flex gap-3">
          {canWrite && (
            <Button disabled={scope == 'couple'} onClick={() => setShowCreateOtherModal(true)}>
              ADICIONAR ATIVO
            </Button>
          )}
        </div>
      </div>

      {showCreateOtherModal && (
        <OtherModal
          clientId={clientId}
          scope={scope}
          onClose={() => setShowCreateOtherModal(false)}
          onSuccess={handleModalSuccess}
        />
      )}

      {selectedOtherId && (
        <OtherModal
          clientId={clientId}
          otherId={selectedOtherId}
          scope={scope}
          onClose={() => setSelectedOtherId(null)}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
};

export default OtherTab;
